import { FlatTreeControl } from "@angular/cdk/tree";
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";
import { FuseSplashScreenService } from "@barebone-fuse-services/splash-screen.service";
import { PlatformQueryService } from "@core/services/platform-query.service";
import { ScenarioCalculationStepModel } from "@features/spreadsheet/components/step-by-step-process/step-by-step-process";
import { FlexSheetService } from "@features/spreadsheet/services/flex-sheet.service";
import { PlatformToolSpreadsheetHandler } from "@features/spreadsheet/services/platform-tool-spreadsheet-handler";
import { SpreadsheetQueryFactory } from "@features/spreadsheet/spreadsheet-query";
import { Store } from "@ngrx/store";
import { IPlatformToolHandler } from "@shared/components/platform-tool/platform-tool-container/platform-tool-component";
import { InvalidateSubject } from "@shared/helpers/invalidate.subject";
import { PlatformQueryResponse } from "@shared/models/platform-query/platform-query";
import { MagicLinkModel } from "@shared/models/platform-tool-models";
import { ParticleState, getCurrentPlatformLink } from "@shared/store/reducers/particle.reduer";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {ScenarioDetailsHandler} from "@features/scenarios-core/services/handlers/scenario-detail-handler.service";

/** Flat node with expandable and level information */
interface GuideFlatNode {
  expandable: boolean;
  title: string;
  level: number;

  command: any;
}
@Component({
    standalone: false,
    selector: "app-spreadsheet-guided-tour",
  templateUrl: "./spreadsheet-guided-tour.component.html",
  styleUrls: ["./spreadsheet-guided-tour.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SpreadsheetGuidedTourComponent implements OnInit, OnDestroy {
  @Input() handler: IPlatformToolHandler;
  platformLink: MagicLinkModel = null;
  private unsubscribe$ = new Subject<void>();
  scenarioCalculationSteps: ScenarioCalculationStepModel[] = [];

  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  queryResponse: PlatformQueryResponse;
  isFetchStepsComplete: boolean = false;
  isAutomaticNavigation: boolean = true;
  disableChecklist: boolean = false;
  isStepJumpEnabled: boolean = false;
  shouldCheckWorkflowStatus : boolean = false;

  guideModel = null;
  invalidatedRightSideComponentData$: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(
    public flexSheetService: FlexSheetService,
    private particleStore: Store<ParticleState>,
    private platformToolSpreadsheetHandler: PlatformToolSpreadsheetHandler,
    private scenarioDetailsHandler: ScenarioDetailsHandler,
    private platformQueryService: PlatformQueryService,
    private fuseSplashScreenService: FuseSplashScreenService
  ) {}

  ngOnInit(): void {
    if (this.handler && this.handler.magicLinkModelValueChanged) {
      this.handler.magicLinkModelValueChanged.pipe(takeUntil(this.unsubscribe$)).subscribe((commamd) => {});
    }
    if (this.scenarioDetailsHandler && this.scenarioDetailsHandler.magicLinkModelValueChanged) {
      this.scenarioDetailsHandler.magicLinkModelValueChanged.pipe(takeUntil(this.unsubscribe$)).subscribe((item) => {
        if(item?.isAutomaticNavigation !== null) {
          this.isAutomaticNavigation = item.isAutomaticNavigation;
        }
      });
    }
    this.particleStore
      .select(getCurrentPlatformLink)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((platformLink: any) => {
        this.platformLink = platformLink;
        this.fetchSteps();
      });

    this.flexSheetService.spreadSheetEvent$.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event.name === "WorkbookChanged") {
        this.reloadGuide();
      }
    });
  }

  ngAfterViewInit(): void {
    this.reloadGuide();
  }

  private reloadGuide() {
    if (this.guideModel && this.guideModel.id === this.flexSheetService.getCurrentWorkBook()?.scenarioGuideModel.id) return;
    this.guideModel = this.flexSheetService.getCurrentWorkBook()?.scenarioGuideModel;
    if(this.guideModel) {
      this.dataSource.data = this.guideModel?.items;
    }
  }

  onLoad() {}

  private _transformer = (node: any, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      title: node.title,
      level: level,
      command: node.platformCommand,
    };
  };

  treeControl = new FlatTreeControl<GuideFlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: GuideFlatNode) => node.expandable;

  navigate(node: any) {
    this.flexSheetService.activeGuideId = node.command.id;
    node.command.projectId = "[PROJECT_ID]";
    node.command.fieldValues["IsReadOnly"] = this.flexSheetService.isReadonly;
    this.platformToolSpreadsheetHandler.executeCommand(node.command);
  }

  fetchSteps() {
    this.isFetchStepsComplete = false;
    const getScenarioChecklistsQuery = SpreadsheetQueryFactory.fetchStepsQuery(this.platformLink);

    this.fuseSplashScreenService.show();
    this.platformQueryService.executeQuery(getScenarioChecklistsQuery).subscribe((res) => {
      this.scenarioCalculationSteps = res.fieldValues["ScenarioCalculationStep"] ? res.fieldValues["ScenarioCalculationStep"] : [];
      this.isAutomaticNavigation = res.fieldValues["IsAutomaticNavigation"] ?? true;
      this.disableChecklist = res.fieldValues["DisableChecklist"] ?? false;
      this.isStepJumpEnabled = res.fieldValues["IsStepJumpEnabled"] ?? false;
      this.shouldCheckWorkflowStatus = res.fieldValues["ShouldCheckWorkflowStatus"] ?? false;
      this.fuseSplashScreenService.hide();
      this.isFetchStepsComplete = true;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
